// src/services/profile.service.ts
import { apiService } from './api.service';

export interface UserProfile {
  id: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  secondLastName?: string;
  fullName?: string;
  photoProfileUrl?: string;
  photoProfileUpdatedAt?: string;
  hasPhoto?: boolean;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  phoneCountryCode?: string;
  alternativePhone?: string;
  birthDate?: string;
  birthPlace?: string;
  birthCountry?: string;
  gender?: string;
  documentType?: string;
  documentNumber?: string;
  documentCountry?: string;
  documentExpiry?: string;
  occupation?: string;
  occupationCode?: string;
  occupationName?: string;
  employmentStatus?: string;
  employerName?: string;
  jobTitle?: string;
  monthlyIncomeRange?: string;
  monthlyIncome?: string;
  incomeSource?: string;
  citizenship?: string;
  secondCitizenship?: string;
  nationalities?: string[];
  primaryNationality?: string;
  currentResidence?: string;
  taxNumber?: string;
  taxCountry?: string;
  taxResidence?: string;
  isPep?: boolean;
  usResidencyStatus?: string;
  euResidencyStatus?: string;
  passports?: any;
  curpRfc?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  currentAddress?: any;
  createdAt?: string;
  updatedAt?: string;
  user?: {
    email?: string;
    accountType?: string;
    accountStatus?: string;
    preferredLanguage?: string;
    createdAt?: string;
    onboardingCompletedAt?: string;
    onboardingCorporate?: any;
    onboardingIndividual?: any;
    onboardingExpress?: any;
  };
  accountType?: string;
  onboardingCorporate?: any;
  corporateInfo?: any;
  representatives?: any[];
  onboardingIndividual?: any;
  onboardingExpress?: any;
}

class ProfileService {
  /**
   * Get current user profile
   */
  async getUserProfile(): Promise<UserProfile> {
    const response = await apiService.get<{ success: boolean; data: UserProfile }>('/profile/me');
    return response.data;
  }

  /**
   * Update user profile fields
   */
  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    const response = await apiService.patch<{ success: boolean; data: UserProfile }>('/profile/me', updates);
    return response.data;
  }

  /**
   * Upload or update profile photo
   */
  async uploadProfilePhoto(file: File): Promise<{ success: boolean; data: { photoUrl: string; photoProfileUrl: string; hasPhoto: boolean } }> {
    const formData = new FormData();
    formData.append('photo', file);
    
    // Use the upload method from apiService which handles multipart/form-data
    const response = await apiService.upload('/profile/photo', formData);
    return response;
  }

  /**
   * Delete profile photo
   */
  async deleteProfilePhoto(): Promise<{ success: boolean; message: string }> {
    return await apiService.delete('/profile/photo');
  }

  /**
   * Get profile photo URL with S3 signed URL
   */
  async getProfilePhotoUrl(): Promise<string | null> {
    try {
      // Primero intentar obtener la URL desde el endpoint específico de foto
      const photoResponse = await apiService.get<{ success: boolean; data: { photoProfileUrl: string | null; hasPhoto: boolean } }>('/profile/photo');
      
      if (photoResponse.data && photoResponse.data.photoProfileUrl) {
        return photoResponse.data.photoProfileUrl;
      }
      
      // Si no funciona, intentar con el perfil completo
      const profile = await this.getUserProfile();
      return profile.photoProfileUrl || null;
    } catch (error) {
      console.error('Error getting profile photo URL:', error);
      return null;
    }
  }
}

export const profileService = new ProfileService();